<template>
  <div>
    <!-- 검색 필터 -->
    <div class="filter-container">
      <input v-model="titleFilter" placeholder="제목 검색" />
      <input v-model="authorFilter" placeholder="작성자 검색" />
    </div>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>제목</th>
          <th>작성자</th>
          <th>작성일</th>
          <th>댓글</th>
          <th>관리</th>
        </tr>
      </thead>
      <tbody>
        <tr
          :class="{ 'hidden-row': post.hidden }"
          v-for="(post, i) in filteredPosts"
          :key="post.id"
          @click="selectPost(post)"
          style="cursor: pointer"
        >
          <td>{{ totalCount - ((currentPage - 1) * pageSize + i) }}</td>
          <td>{{ post.title }}</td>
          <td>{{ post.author.name }}</td>
          <td>{{ formatDate(post.createdAt) }}</td>
          <td>{{ post.commentCount }}</td>
          <td>
            <button @click.stop="deletePost(post)">삭제</button>
            <button @click.stop="toggleHidePost(post)">
              {{ post.hidden ? '복원' : '숨김' }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1">이전</button>
      <span>{{ currentPage }}</span>
      <button @click="nextPage" :disabled="currentPage >= maxPage">다음</button>
    </div>

    <div v-if="selectedPost" class="post-detail">
      <h4>{{ selectedPost.title }}</h4>
      <p>{{ selectedPost.content }}</p>
      <p>
        <small>
          작성자: {{ selectedPost.author.name }} / 작성일:
          {{ formatDate(selectedPost.createdAt) }}
        </small>
      </p>

      <p class="bold">댓글</p>
      <ul>
        <li v-for="(comment, i) in comments" :key="i">
          {{ formatDate(comment.createdAt) }} |
          {{ comment.author?.name || '익명' }}:
          {{ comment.content || '[내용 없음]' }}
          <button
            @click="deleteComment(comment.id)"
            style="margin-left: 0.5rem; font-size: 0.75rem"
          >
            삭제
          </button>
        </li>
      </ul>

      <!-- 관리자 댓글 입력 -->
      <div class="admin-comment">
        <input v-model="adminComment" placeholder="관리자 댓글 입력..." />
        <button @click="addAdminComment" class="primary-button">등록</button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from 'vue'
  import {
    collection,
    getDocs,
    query,
    orderBy,
    deleteDoc,
    doc,
    updateDoc,
    addDoc,
  } from 'firebase/firestore'
  import { db } from '@/firebase'

  const allPosts = ref([])
  const posts = ref([])
  const selectedPost = ref(null)
  const comments = ref([])
  const adminComment = ref('')
  const titleFilter = ref('')
  const authorFilter = ref('')
  const pageSize = 10
  const currentPage = ref(1)
  const totalCount = ref(0)
  const loading = ref(false)

  const filteredPosts = computed(() => {
    const start = (currentPage.value - 1) * pageSize
    const end = start + pageSize
    return allPosts.value
      .filter(
        (p) =>
          (!titleFilter.value || p.title?.includes(titleFilter.value)) &&
          (!authorFilter.value || p.author?.name?.includes(authorFilter.value)),
      )
      .slice(start, end)
  })

  const maxPage = computed(() => {
    const filtered = allPosts.value.filter(
      (p) =>
        (!titleFilter.value || p.title?.includes(titleFilter.value)) &&
        (!authorFilter.value || p.author?.name?.includes(authorFilter.value)),
    )
    return Math.ceil(filtered.length / pageSize)
  })

  onMounted(async () => {
    loading.value = true
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'))
    const snapshot = await getDocs(q)
    allPosts.value = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    totalCount.value = allPosts.value.length
    loading.value = false
  })

  const formatDate = (ts) => {
    const d = ts?.toDate?.() ?? new Date(ts)
    if (isNaN(d)) return '-'
    const pad = (n) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  }

  const nextPage = () => {
    if (currentPage.value < maxPage.value) currentPage.value++
  }

  const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--
  }

  const selectPost = async (post) => {
    selectedPost.value = post
    const ref = collection(db, 'posts', post.id, 'comments')
    const snapshot = await getDocs(ref)
    comments.value = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  }

  const addAdminComment = async () => {
    if (!adminComment.value.trim()) return
    const ref = collection(db, 'posts', selectedPost.value.id, 'comments')
    await addDoc(ref, {
      content: adminComment.value,
      author: { name: '관리자', uid: 'admin' },
      createdAt: new Date(),
    })

    const postRef = doc(db, 'posts', selectedPost.value.id)
    const updatedCount = (selectedPost.value.commentCount ?? 0) + 1
    await updateDoc(postRef, { commentCount: updatedCount })
    selectedPost.value.commentCount = updatedCount

    const index = allPosts.value.findIndex(
      (p) => p.id === selectedPost.value.id,
    )
    if (index !== -1) {
      allPosts.value[index].commentCount = updatedCount
    }

    adminComment.value = ''
    await selectPost(selectedPost.value)
  }

  const deletePost = async (post) => {
    if (!confirm('이 게시글을 삭제하시겠습니까?')) return
    const ref = collection(db, 'posts', post.id, 'comments')
    const snapshot = await getDocs(ref)
    if (!snapshot.empty) {
      alert('댓글이 있는 경우 삭제가 불가합니다.')
      return
    }
    await deleteDoc(doc(db, 'posts', post.id))
    allPosts.value = allPosts.value.filter((p) => p.id !== post.id)
    totalCount.value--
  }

  const deleteComment = async (commentId) => {
    if (!confirm('이 댓글을 삭제하시겠습니까?')) return
    const commentRef = doc(
      db,
      'posts',
      selectedPost.value.id,
      'comments',
      commentId,
    )
    await deleteDoc(commentRef)

    const postRef = doc(db, 'posts', selectedPost.value.id)
    const updatedCount = Math.max((selectedPost.value.commentCount ?? 1) - 1, 0)
    await updateDoc(postRef, { commentCount: updatedCount })
    selectedPost.value.commentCount = updatedCount

    const index = allPosts.value.findIndex(
      (p) => p.id === selectedPost.value.id,
    )
    if (index !== -1) {
      allPosts.value[index].commentCount = updatedCount
    }

    await selectPost(selectedPost.value)
  }

  const toggleHidePost = async (post) => {
    const ref = doc(db, 'posts', post.id)
    const newHidden = !post.hidden
    await updateDoc(ref, { hidden: newHidden })
    post.hidden = newHidden
  }
</script>

<style scoped>
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    margin-top: 1rem;
  }
  th,
  td {
    padding: 0.4rem 0;
    border: 1px solid #ccc;
    text-align: center;
  }
  .post-detail {
    margin-top: 2rem;
    padding: 1rem;
    border: 1px solid #ccc;
    background: #f9f9f9;
  }
  .post-detail p {
    font-size: 0.9rem;
  }
  .bold {
    font-weight: bold;
  }
  ul {
    padding-left: 1rem;
  }
  li {
    font-size: 0.85rem;
    margin-bottom: 0.4rem;
  }
  .hidden-row {
    opacity: 0.5;
    background-color: #f0f0f0;
  }
  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin: 1rem 0;
  }
  .filter-container {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .filter-container input {
    padding: 0.5rem;
    font-size: 0.9rem;
    flex: 1;
  }
  .admin-comment {
    margin-top: 1rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .admin-comment input {
    flex: 1;
    padding: 0.5rem;
    font-size: 0.9rem;
  }
  .primary-button {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
  }
  .primary-button:hover {
    background-color: #45a049;
  }
</style>
